import { useState, useEffect } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import toast, { Toaster } from 'react-hot-toast';
import ReceptionDetailsView from './ReceptionDetailsView'; // ← ton composant détail

interface Reception {
  id: number;
  numero: string;
  bc_numero: string;
  fournisseur_nom: string;
  date_reception: string;
  utilisateur_nom: string;
  total_commandee: number;
  total_recue_cumule: number;
  quantite_dans_ce_br: number;
  total_restante: number;
  has_non_conformite: boolean;
  lignes?: any[];
  bc_fournisseur?: any;
  observations?: string;
  emplacement_nom?: string;
}

interface ReceptionsListProps {
  onViewDetails: (reception: Reception) => void;
  onCreateRetour?: (reception: Reception) => void; // Optionnel si tu veux gérer le retour ailleurs
}

export default function ReceptionsList({ onViewDetails, onCreateRetour }: ReceptionsListProps) {
  const [receptions, setReceptions] = useState<Reception[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('Tous');
  const [selectedReception, setSelectedReception] = useState<any>(null);
  // États pour les actions dynamiques
  const [loadingDetail, setLoadingDetail] = useState<number | null>(null);
  const [loadingPrint, setLoadingPrint] = useState<number | null>(null);
  const [loadingRetour, setLoadingRetour] = useState<number | null>(null);

  useEffect(() => {
    fetchReceptions();
  }, []);

  const fetchReceptions = async () => {
    try {
      const res = await getResources('bons-reception', {
        params: {
          with: 'bc.fournisseur,bc.lignes.produit,lignes.produit,utilisateur,emplacement'
        }
      });

      const mapped: Reception[] = res.data.data.map((br: any) => {
        const bc = br.bc;

        return {
          id: br.id,
          numero: br.numero,
          bc_numero: bc?.numero ?? '—',
          fournisseur_nom: bc?.fournisseur?.raison_sociale ?? '—',
          date_reception: br.date_reception,
          utilisateur_nom: br.utilisateur?.name ?? 'Système',
          total_commandee: br.total_commandee,
          total_recue_cumule: br.total_recue_cumule,
          quantite_dans_ce_br: br.quantite_dans_ce_br || br.total_recue_cumule,
          total_restante: br.total_restante,
          has_non_conformite: br.has_non_conformite ?? false,
          lignes: br.lignes,
          bc_fournisseur: bc,
          observations: br.observations,
          emplacement_nom: br.emplacement_nom,
        };
      });

      setReceptions(mapped);
    } catch (err) {
      toast.error("Erreur lors du chargement des réceptions");
    } finally {
      setLoading(false);
    }
  };

  const getStatutReception = (r: Reception) => {
    if (r.total_recue_cumule >= r.total_commandee) {
      return r.total_recue_cumule > r.total_commandee ? 'Excédentaire' : 'Complète';
    }
    return 'Partielle';
  };

  const filteredReceptions = receptions.filter(r => {
    const search = searchTerm.toLowerCase();
    const matchesSearch =
      r.numero.toLowerCase().includes(search) ||
      r.bc_numero.toLowerCase().includes(search) ||
      r.fournisseur_nom.toLowerCase().includes(search);

    const statut = getStatutReception(r);
    const matchesStatus = filterStatus === 'Tous' || statut === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (statut: string) => {
    switch (statut) {
      case 'Complète': return 'bg-green-100 text-green-800';
      case 'Partielle': return 'bg-orange-100 text-orange-800';
      case 'Excédentaire': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date: string) => new Date(date).toLocaleDateString('fr-FR');

  // === IMPRESSION BON DE RÉCEPTION - VERSION UNE SEULE PAGE A4 ===
  const printBR = async (reception: Reception) => {
    setLoadingPrint(reception.id);
    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error("Impossible d'ouvrir la fenêtre d'impression");
        return;
      }

      const lignes = reception.lignes || [];

      const totalHT = reception.bc_fournisseur.total_ht || 0;
      const totalTVA = reception.bc_fournisseur.total_tva || 0;
      const totalTTC = reception.bc_fournisseur.total_ttc || 0;

      const html = `
        <!DOCTYPE html>
        <html lang="fr">
        <head>
          <meta charset="utf-8">
          <title>BR - ${reception.numero}</title>
          <style>
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
              color: #1a3c34;
              font-size: 13px;
              line-height: 1.4;
            }
            .container {
              max-width: 210mm;
              min-height: 297mm;
              margin: 0 auto;
              background: white;
              padding: 15px;
              box-sizing: border-box;
            }
            .header {
              background: linear-gradient(135deg, #059669, #10b981);
              color: white;
              padding: 12px;
              text-align: center;
              border-radius: 8px;
              margin-bottom: 12px;
            }
            .logo {
              height: 50px;
              margin-bottom: 8px;
              filter: brightness(0) invert(1);
            }
            .header h1 {
              margin: 0;
              font-size: 22px;
              font-weight: 600;
            }
            .header h2 {
              margin: 4px 0 0;
              font-size: 18px;
              opacity: 0.95;
            }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
              margin-bottom: 12px;
              font-size: 12px;
            }
            .info-item {
              background: #f0fdf4;
              padding: 8px;
              border-radius: 6px;
            }
            .info-label {
              font-weight: 600;
              color: #065f46;
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 0.4px;
            }
            .info-value {
              font-weight: 500;
              margin-top: 3px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 12px 0;
              font-size: 12px;
            }
            th {
              background: #d1fae5;
              color: #065f46;
              font-weight: 600;
              padding: 8px 6px;
              text-align: left;
              font-size: 11px;
              border-bottom: 2px solid #10b981;
            }
            td {
              padding: 8px 6px;
              border-bottom: 1px solid #a7f3d0;
              vertical-align: top;
            }
            .text-right { text-align: right; }
            .non-conforme {
              background: #fee2e2 !important;
            }
            .text-red { color: #dc2626; font-weight: 600; }
            .qty-summary {
              background: #f0fdf4;
              padding: 10px;
              border-radius: 6px;
              margin: 12px 0;
              font-size: 13px;
            }
            .total-section {
              float: right;
              width: 280px;
              border: 1px solid #a7f3d0;
              border-radius: 6px;
              overflow: hidden;
              margin-top: 10px;
              font-size: 13px;
            }
            .total-section table {
              width: 100%;
              background: #f8fffa;
              margin: 0;
            }
            .total-row td {
              padding: 8px 10px;
              font-weight: 600;
            }
            .total-row-strong {
              background: #10b981 !important;
              color: white;
              font-size: 14px;
            }
            .total-row-strong td {
              padding: 10px;
              font-weight: 700;
            }
            .status-complete {
              color: #16a34a;
              font-weight: bold;
            }
            .signature {
              margin-top: 20px;
              display: flex;
              justify-content: space-between;
              padding: 0 20px;
              clear: both;
              page-break-inside: avoid;
            }
            .signature-line {
              border-top: 1.5px solid #059669;
              width: 220px;
              padding-top: 6px;
              text-align: center;
              font-size: 12px;
              color: #065f46;
            }
            .footer {
              margin-top: 15px;
              text-align: center;
              font-size: 11px;
              color: #6b7280;
              clear: both;
              page-break-inside: avoid;
            }
            @media print {
              body { padding: 10px; margin: 0; }
              .container { padding: 10px; box-shadow: none; }
              @page { size: A4; margin: 8mm; }
              table { page-break-inside: avoid; }
              .signature, .footer { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <img src="https://via.placeholder.com/180x50/059669/ffffff?text=TON+LOGO" alt="Logo" class="logo">
              <h1>Bon de Réception</h1>
              <h2>N° ${reception.numero}</h2>
            </div>

            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Bon de Commande</div>
                <div class="info-value">${reception.bc_numero || '—'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Fournisseur</div>
                <div class="info-value">${reception.fournisseur_nom}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Date réception</div>
                <div class="info-value">${formatDate(reception.date_reception)}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Réceptionné par</div>
                <div class="info-value">${reception.utilisateur_nom}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Emplacement</div>
                <div class="info-value">${reception.emplacement_nom || '—'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Observations</div>
                <div class="info-value">${reception.observations || 'Aucune'}</div>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>Produit</th>
                  <th class="text-right">Qté cmd</th>
                  <th class="text-right">Reçu</th>
                  <th class="text-right">Conforme</th>
                  <th class="text-right">Rejetée</th>
                  <th>Motif</th>
                </tr>
              </thead>
              <tbody>
                ${lignes.map((l: any) => {
                  const conforme = l.quantite_recue || 0;
                  const rejete = l.quantite_rejetee || 0;
                  const totalRecu = conforme + rejete;
                  const isNonConforme = rejete > 0;
                  return `
                    <tr ${isNonConforme ? 'class="non-conforme"' : ''}>
                      <td><strong>${l.produit?.designation || '—'}</strong></td>
                      <td class="text-right">${l.quantite_commandee || 0}</td>
                      <td class="text-right">${totalRecu}</td>
                      <td class="text-right">${conforme}</td>
                      <td class="text-right text-red">${rejete}</td>
                      <td style="font-size:11px;">${isNonConforme 
                        ? (l.motif_rejet || '—') + (l.action_non_conformite ? '<br><em>' + l.action_non_conformite + '</em>' : '')
                        : '—'
                      }</td>
                    </tr>`;
                }).join('')}
              </tbody>
            </table>

            <div class="qty-summary">
              <strong>Résumé :</strong>&nbsp;&nbsp;
              Cmd : ${reception.total_commandee} u. | 
              Reçu cumulé : ${reception.total_recue_cumule} u. | 
              Ce BR : ${reception.quantite_dans_ce_br} u. | 
              ${reception.total_restante > 0 
                ? `En attente : ${reception.total_restante} u.`
                : '<span class="status-complete">Réception complète</span>'
              }
            </div>

            <div class="total-section">
              <table>
                <tbody>
                  <tr class="total-row">
                    <td>Total HT (BC)</td>
                    <td class="text-right">${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(totalHT)} ${reception.devise || 'GNF'}</td>
                  </tr>
                  <tr class="total-row">
                    <td>TVA</td>
                    <td class="text-right">${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(totalTVA)} ${reception.devise || 'GNF'}</td>
                  </tr>
                  <tr class="total-row-strong">
                    <td><strong>Total TTC</strong></td>
                    <td class="text-right"><strong>${new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2 }).format(totalTTC)} ${reception.devise || 'GNF'}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="signature">
              <div class="signature-line">
                Réceptionné par<br><em>${reception.utilisateur_nom}</em>
              </div>
              <div class="signature-line">
                Contrôle / Approuvé par
              </div>
            </div>

            <div class="footer">
              Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} 
              — BR ${reception.numero} (BC ${reception.bc_numero})
            </div>
          </div>
        </body>
        </html>
      `;

      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();

      await new Promise(resolve => setTimeout(resolve, 600));
      printWindow.print();
      // printWindow.close(); // Décommenter si tu veux fermer automatiquement

      toast.success(`BR ${reception.numero} imprimé (1 page)`);
    } catch (err) {
      console.error(err);
      toast.error("Erreur d'impression");
    } finally {
      setLoadingPrint(null);
    }
  };

  const handleViewDetails = async (reception: Reception) => {
    setLoadingDetail(reception.id);

    try {
      const response = await getResources(
        `bons-reception/${reception.id}`,
        {
          params: {
            with: [
              'bc.fournisseur',
              'bc.lignes.produit',
              'lignes.produit',
              'utilisateur',
              'emplacement',
              'bonRetour.lignes.produit',
            ].join(','),
          },
        }
      );

      const receptionDetail = response.data.data;

      // Calculs JS
      const totalCommandee = Array.isArray(receptionDetail?.bc?.lignes)
      ? receptionDetail.bc.lignes.reduce(
          (acc: number, l: any) => acc + Number(l.quantite_commandee ?? 0),
          0
        )
      : 0;

      const quantiteDansCeBR =
        receptionDetail.lignes?.reduce(
          (acc: number, l: any) => acc + (l.quantite_recue ?? 0),
          0
        ) ?? 0;

      const totalRecuCumule = await calculerRecuCumule(receptionDetail.bc?.id);

      // 👉 TRANSFORMATION DES LIGNES POUR LE MODAL
      const lignesTransformees = receptionDetail.lignes.map((l: any) => ({
        id: l.id,
        produit: l.produit?.designation || "Produit inconnu",
        quantite_commandee: totalCommandee,
        quantite_recue: l.quantite_recue,
        quantite_rejetee: l.quantite_rejetee,
        motif_rejet: l.motif_rejet,
        action_non_conformite: l.action_non_conformite,
      }));

      // 👉 ON ENVOIE EXACTEMENT LE FORMAT ATTENDU PAR LE MODAL
      const receptionEnrichie: Reception = {
        id: receptionDetail.id,
        numero: receptionDetail.numero,
        date_reception: receptionDetail.date_reception,
        bc_numero: receptionDetail.bc?.numero || "",
        fournisseur: receptionDetail.bc?.fournisseur?.raison_sociale || "",
        utilisateur_nom: receptionDetail.utilisateur?.name || "",
        emplacement_nom: receptionDetail.emplacement?.libelle || null,
        observations: receptionDetail.observations || "",
        has_non_conformite: receptionDetail.lignes.some((l: any) => l.quantite_rejetee > 0),

        total_commandee: totalCommandee,
        total_recue_cumule: totalRecuCumule,
        quantite_dans_ce_br: quantiteDansCeBR,
        total_restante: totalCommandee - quantiteDansCeBR,

        lignes: lignesTransformees,

        bon_retour: receptionDetail.bon_retour || null,
      };

    setSelectedReception(receptionEnrichie);

    } catch (err: any) {
      toast.error(
        err.response?.data?.message || "Impossible de charger les détails du bon de réception"
      );
    } finally {
      setLoadingDetail(null);
    }
  };

// Bonus : fonction pour calculer le total reçu cumulé (tous les BR du même BC)
const calculerRecuCumule = async (bcId: number | null) => {
  if (!bcId) return 0;
  try {
    const res = await getResources(`bons-reception`, {
      params: { bc_id: bcId },
    });
    return res.data.data.reduce((sum: number, br: any) => {
      return sum + (br.lignes?.sum('quantite_recue') || 0);
    }, 0);
  } catch {
    return 0;
  }
};

  // === BON DE RETOUR DYNAMIQUE ===
  const printBonRetour = (bonRetour: any) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const lignes = bonRetour.lignes || [];

    const html = `
      <!DOCTYPE html>
      <html lang="fr">
      <head>
        <meta charset="utf-8">
        <title>Bon de Retour ${bonRetour.numero}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; font-size: 13px; line-height: 1.6; }
          .header { text-align: center; margin-bottom: 3px solid #dc2626; padding-bottom: 15px; }
          .title { color: #dc2626; font-size: 28px; font-weight: bold; margin: 0; }
          .info { display: flex; justify-content: space-between; margin: 30px 0; background: #fee2e2; padding: 15px; border-radius: 8px; }
          table { width: 100%; border-collapse: collapse; margin: 30px 0; }
          th, td { border: 1px solid #666; padding: 12px; text-align: left; }
          th { background: #fee2e2; color: #991b1b; font-weight: bold; }
          .text-center { text-align: center; }
          .text-right { text-align: right; }
          .footer { margin-top: 60px; padding-top: 20px; border-top: 2px dashed #dc2626; }
          .signature { margin-top: 80px; display: flex; justify-content: space-between; }
          .signature div { width: 280px; text-align: center; padding-top: 10px; border-top: 1px solid #000; }
          .highlight { background: #fee2e2; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 class="title">BON DE RETOUR FOURNISSEUR</h1>
          <h2>N° ${bonRetour.numero}</h2>
        </div>

        <div class="info">
          <div>
            <strong>BR d'origine :</strong> ${bonRetour.br?.numero || '—'}<br>
            <strong>Fournisseur :</strong> ${bonRetour.fournisseur?.raison_sociale || '—'}<br>
            <strong>Date retour :</strong> ${new Date(bonRetour.date_retour).toLocaleDateString('fr-FR')}<br>
            <strong>Émis par :</strong> ${bonRetour.utilisateur?.name || 'Système'}
          </div>
          <div class="text-right">
            ${bonRetour.emplacement ? `<strong>Emplacement :</strong> ${bonRetour.emplacement.libelle}<br>` : ''}
            <strong>Observations :</strong><br>
            ${bonRetour.observations || 'Aucune observation'}
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Produit</th>
              <th class="text-center">Quantité retournée</th>
              <th class="text-center">Prix unitaire HT</th>
              <th>Motif du retour</th>
            </tr>
          </thead>
          <tbody>
            ${lignes.map((l: any) => `
              <tr>
                <td><strong>${l.produit?.designation || '—'}</strong></td>
                <td class="text-center">${l.quantite}</td>
                <td class="text-center">${parseFloat(l.prix_unitaire_ht || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2})} GNF</td>
                <td>${l.motif || '<em style="color:#999">Non précisé</em>'}</td>
              </tr>
            `).join('')}
          </tbody>
          <tfoot>
            <tr class="highlight">
              <td colspan="1" class="text-right font-bold">TOTAL RETOURNÉ :</td>
              <td class="text-center font-bold" style="font-size:16px;color:#dc2626">
                ${lignes.reduce((s: number, l: any) => s + l.quantite, 0)} unité(s)
              </td>
              <td></td>
              <td></td>
            </tr>
          </tfoot>
        </table>

        <div class="footer">
          <p><em>Ce document fait foi de retour de marchandise non conforme auprès du fournisseur.</em></p>
        </div>

        <div class="signature">
          <div>Cachet & Signature Fournisseur</div>
          <div>Cachet & Signature Responsable</div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 600);
    printWindow.onafterprint = () => printWindow.close();
  };

  // === BON DE RETOUR DYNAMIQUE ===
const handleFetchRetour = async (reception: Reception) => {
  setLoadingRetour(reception.id);

  try {
    // 1️⃣ Vérifier qu'il y a des lignes rejetées
    const lignesNonConformes = reception.lignes.filter((l: any) => (l.quantite_rejetee || 0) > 0);

    if (lignesNonConformes.length === 0) {
      toast.error('Aucune marchandise rejetée à retourner');
      return;
    }

    // 2️⃣ Appel API vers Laravel pour récupérer le bon de retour du BR
    const response = await getResources(
      `bons-reception/bons-retour-fournisseur/${reception.id}`
    );

    const bonRetour = response.data.data;

    if (!bonRetour) {
      toast.error('Aucun bon de retour trouvé pour cette réception');
      return;
    }

    // 3️⃣ On ouvre l'impression du bon de retour existant
    printBonRetour(bonRetour);

    toast.success(`Bon de retour ${bonRetour.numero} récupéré et envoyé à l'impression !`);

  } catch (err: any) {
    console.error(err);
    const message = err.response?.data?.message || "Erreur lors de la récupération du bon de retour";
    toast.error(message);
  } finally {
    setLoadingRetour(null);
  }
};


  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-16 text-center">
        <i className="ri-loader-4-line text-5xl text-gray-400 animate-spin"></i>
        <p className="mt-4 text-gray-600">Chargement des réceptions...</p>
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" />

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* HEADER & FILTRES (inchangés) */}
        <div className="p-6 border-b border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{receptions.length}</div>
              <div className="text-sm text-gray-500">Total Réceptions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {receptions.filter(r => getStatutReception(r) === 'Complète').length}
              </div>
              <div className="text-sm text-gray-500">Complètes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {receptions.filter(r => getStatutReception(r) === 'Partielle').length}
              </div>
              <div className="text-sm text-gray-500">Partielles</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {receptions.filter(r => r.has_non_conformite).length}
              </div>
              <div className="text-sm text-gray-500">Non-conformités</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Rechercher par numéro BR, BC ou fournisseur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
              >
                <option value="Tous">Tous les statuts</option>
                <option value="Complète">Complète</option>
                <option value="Partielle">Partielle</option>
                <option value="Excédentaire">Excédentaire</option>
              </select>
            </div>
          </div>
        </div>

        {/* TABLEAU */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bon de Réception</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bon de Commande</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fournisseur</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Réception</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantités</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredReceptions.map((r) => {
                const statut = getStatutReception(r);
                const isDetailLoading = loadingDetail === r.id;
                const isPrintLoading = loadingPrint === r.id;
                const isRetourLoading = loadingRetour === r.id;

                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{r.numero}</div>
                        <div className="text-sm text-gray-500">Par: {r.utilisateur_nom}</div>
                        {r.has_non_conformite && (
                          <div className="text-xs text-red-600 font-medium mt-1">
                            Non-conformités détectées
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{r.bc_numero}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{r.fournisseur_nom}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(r.date_reception)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="text-gray-900">
                          Cumulé: <span className="font-bold text-green-600">{r.total_recue_cumule}</span> / {r.total_commandee}
                        </div>
                        <div className="text-xs text-gray-500">
                          Dans ce BR: +{r.quantite_dans_ce_br}
                        </div>
                        {r.total_restante > 0 && (
                          <div className="text-orange-600 text-xs mt-1">
                            En attente: {r.total_restante}
                          </div>
                        )}
                        {r.total_recue_cumule > r.total_commandee && (
                          <div className="text-purple-600 text-xs mt-1">
                            Excédent: {r.total_recue_cumule - r.total_commandee}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(statut)}`}>
                        {statut}
                      </span>
                    </td>

                    {/* ACTIONS DYNAMIQUES */}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-3">
                        {/* DÉTAIL */}
                        <button
                          onClick={() => handleViewDetails(r)}
                          disabled={isDetailLoading}
                          className={`p-2 rounded transition ${
                            isDetailLoading
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-blue-600 hover:text-blue-900 hover:bg-blue-50'
                          }`}
                          title="Voir le détail"
                        >
                          {isDetailLoading ? (
                            <i className="ri-loader-4-line animate-spin text-lg"></i>
                          ) : (
                            <i className="ri-eye-line text-lg"></i>
                          )}
                        </button>

                        {/* IMPRIMER */}
                        <button
                          onClick={() => printBR(r)}
                          disabled={isPrintLoading}
                          className={`p-2 rounded transition ${
                            isPrintLoading
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                          }`}
                          title="Imprimer le bon de réception"
                        >
                          {isPrintLoading ? (
                            <i className="ri-loader-4-line animate-spin text-lg"></i>
                          ) : (
                            <i className="ri-printer-line text-lg"></i>
                          )}
                        </button>

                        {/* BON DE RETOUR */}
                        {r.has_non_conformite && (
                          <button
                            onClick={() => handleFetchRetour(r)}
                            disabled={isRetourLoading}
                            className={`p-2 rounded transition ${
                              isRetourLoading
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-red-600 hover:text-red-900 hover:bg-red-50'
                            }`}
                            title="Créer bon de retour"
                          >
                            {isRetourLoading ? (
                              <i className="ri-loader-4-line animate-spin text-lg"></i>
                            ) : (
                              <i className="ri-arrow-go-back-line text-lg"></i>
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* MODALE DE DÉTAIL */}
        {selectedReception && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto">
              <ReceptionDetailsView
                reception={selectedReception}
                onClose={() => setSelectedReception(null)}
                onCreateRetour={handleFetchRetour}
              />
            </div>
          </div>
        )}

        {filteredReceptions.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-inbox-line text-4xl text-gray-400 mb-4"></i>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune réception trouvée</h3>
            <p className="text-gray-500">
              {searchTerm ? 'Aucun résultat.' : 'Les réceptions apparaîtront ici.'}
            </p>
          </div>
        )}
      </div>
    </>
  );
}